export function stripPhoneDigits(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '')
  // US country code: strip leading "1" when input is exactly 11 digits.
  if (digits.length === 11 && digits[0] === '1') return digits.slice(1)
  // Hard cap at 10 — excess digits past a filled field are dropped, not shifted in.
  return digits.slice(0, 10)
}

export function isValidPhone(raw) {
  return stripPhoneDigits(raw).length === 10
}

export function formatPhoneStrict(raw) {
  const digits = stripPhoneDigits(raw)
  let formatted = ''
  if (digits.length === 0) {
    formatted = ''
  } else if (digits.length < 4) {
    formatted = `(${digits}`
  } else if (digits.length < 7) {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  } else {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return { formatted, digits, isValid: digits.length === 10 }
}

const EMAIL_RE =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}$/

export function isValidEmail(raw) {
  const v = String(raw ?? '').trim()
  if (!v) return false
  return EMAIL_RE.test(v)
}

const ZIP_RE = /^\d{5}(-\d{4})?$/

export function isValidZip(raw) {
  return ZIP_RE.test(String(raw ?? '').trim())
}

// Format zip display: "12345" for <6 digits, "12345-6789" for 6+.
export function formatZip(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '').slice(0, 9)
  if (digits.length < 6) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

const EMAIL_TYPO_MAP = {
  gmial: 'gmail',
  gmai: 'gmail',
  gmil: 'gmail',
  yaho: 'yahoo',
  yhoo: 'yahoo',
  hotmial: 'hotmail',
  hotmai: 'hotmail',
  outlok: 'outlook',
  outloo: 'outlook',
}

export function suggestEmailCorrection(email) {
  const v = String(email ?? '').trim().toLowerCase()
  const at = v.indexOf('@')
  if (at <= 0) return null
  const local = v.slice(0, at)
  const domain = v.slice(at + 1)
  const dot = domain.indexOf('.')
  if (dot <= 0) return null
  const host = domain.slice(0, dot)
  const tld = domain.slice(dot + 1)
  const fixed = EMAIL_TYPO_MAP[host]
  if (!fixed) return null
  return `${local}@${fixed}.${tld}`
}

const ROLE_LOCALS = new Set([
  'info',
  'admin',
  'support',
  'contact',
  'hello',
  'noreply',
  'no-reply',
  'office',
  'billing',
])

export function isRoleEmail(email) {
  const v = String(email ?? '').trim().toLowerCase()
  const at = v.indexOf('@')
  if (at <= 0) return false
  return ROLE_LOCALS.has(v.slice(0, at))
}

export function validateName(raw) {
  const v = String(raw ?? '').trim()
  if (v.length < 2 || !/\s/.test(v)) {
    return { isValid: false, message: 'Add a full name (first and last)' }
  }
  return { isValid: true, message: '' }
}

export function requireAtLeastOne(arr, predicate) {
  if (!Array.isArray(arr) || typeof predicate !== 'function') return false
  return arr.some((item) => {
    try {
      return Boolean(predicate(item))
    } catch {
      return false
    }
  })
}

export function flattenErrorCount(errs) {
  if (!errs || typeof errs !== 'object') return 0
  let count = 0
  const walk = (v) => {
    if (!v || typeof v !== 'object') return
    if (typeof v.message === 'string') {
      count += 1
      return
    }
    if (Array.isArray(v)) {
      v.forEach(walk)
      return
    }
    for (const k of Object.keys(v)) {
      if (k === 'ref' || k === 'types') continue
      walk(v[k])
    }
  }
  walk(errs)
  return count
}

// Human labels for the array fields used across steps, so an error at
// `owners.0.phoneNo` reads "Owner 1" instead of the raw path.
const ARRAY_ITEM_LABELS = {
  owners: 'Owner',
  providers: 'Provider',
  teams: 'Team member',
  teamMembers: 'Team member',
  locations: 'Location',
  kits: 'Kit',
}

function prettyPrefix(path) {
  const parts = String(path).split('.')
  for (let i = 0; i < parts.length - 1; i += 1) {
    const idx = Number(parts[i + 1])
    if (Number.isInteger(idx) && idx >= 0) {
      const base =
        ARRAY_ITEM_LABELS[parts[i]] ||
        parts[i].replace(/s$/, '').replace(/^./, (c) => c.toUpperCase())
      return `${base} ${idx + 1}`
    }
  }
  return ''
}

// Walk a react-hook-form errors object and collect readable, de-duplicated
// lines like "Owner 1: Phone must be 10 digits". Mirrors flattenErrorCount's
// traversal but gathers messages (with a path-derived prefix) instead of a
// count. Used to render the visible error-summary banner on every step.
export function collectErrorMessages(errs) {
  const out = []
  const seen = new Set()
  const add = (path, message) => {
    if (!message) return
    const prefix = prettyPrefix(path)
    const text = prefix ? `${prefix}: ${message}` : message
    if (seen.has(text)) return
    seen.add(text)
    out.push(text)
  }
  const walk = (v, path) => {
    if (!v || typeof v !== 'object') return
    if (Array.isArray(v)) {
      if (typeof v.message === 'string') add(path, v.message)
      if (v.root && typeof v.root.message === 'string') add(path, v.root.message)
      v.forEach((item, i) => walk(item, path ? `${path}.${i}` : String(i)))
      return
    }
    if (typeof v.message === 'string') {
      add(path, v.message)
      return
    }
    for (const k of Object.keys(v)) {
      if (k === 'ref' || k === 'types' || k === 'root' || k === 'message') continue
      walk(v[k], path ? `${path}.${k}` : k)
    }
  }
  walk(errs, '')
  return out
}

export function uniqueEmailsAcross({ owners, providers, teams } = {}) {
  const groups = new Map()
  const push = (group, list) => {
    ;(list || []).forEach((item, index) => {
      const e = String(item?.email ?? '').trim().toLowerCase()
      if (!e) return
      if (!groups.has(e)) groups.set(e, [])
      groups.get(e).push({ group, index })
    })
  }
  push('owners', owners)
  push('providers', providers)
  push('teams', teams)

  const dupes = new Map()
  for (const [email, locations] of groups.entries()) {
    if (locations.length > 1) dupes.set(email, locations)
  }
  return dupes
}
